import React, { useState, useEffect, useMemo } from "react";
import { AlertTriangle, Ban, Trash2 } from "lucide-react";
import { CAREER_TREE } from "@/data/career-tree";
import Joyride, { STATUS } from "react-joyride";
import { useNavigate } from "react-router";
import { Sidebar } from "../components/ui/sidebar";
import { CareerPathHeader } from "../components/ui/carreer-path/career-path-header";
import { CareerPathBanner } from "../components/ui/career-path-banner";
import { CareerPathCard } from "../components/ui/carreer-path/career-path-card";
import { AddCareerPathCard } from "../components/ui/carreer-path/add-career-path";
import { UniversalSearch } from "../components/ui/universal-search";
import ProtectedRoute from "../components/ProtectedRoute";
import { StorageService } from "@/hooks/services/storageService";
import { AuthStore } from "@/hooks/stores/AuthStore";
import { StudentStore } from "@/hooks/stores/StudentStore";
import api from "@/hooks/api/apiClient";
import config from "../../config";
import { pathEngine } from "@/hooks/usePathEngine";
import { toast } from "sonner";

const EMPTY_ARR = [];

// ─── Creative deletion messages ──────────────────────────────────────────────
const DELETE_MESSAGES = [
  "Path released into the void 🌌 — the universe has been notified.",
  "Poof! That career path has left the chat 💨",
  "Yeet! That one's gone. No regrets (probably) 🚀",
  "Career path? What career path? 👀 We saw nothing.",
  "Like a sandcastle at high tide — washed away! 🌊",
  "That path has officially clocked out. Benefits end Friday. 📦",
  "Deleted faster than your last situationship ended 💀",
  "Gone like your motivation on a Monday morning 😂",
  "That career path has left the building. Thank you, next. 🎤",
  "Removed! Your future self will probably thank you 🙏",
  "Path? Annihilated. You're basically a career ninja 🥷",
  "That one's been archived in the Museum of Abandoned Dreams 🏛️",
  "Splat! Career path = gone. The era has ended. 🦋",
  "Sayonara! That path is now a distant memory ✌️",
  "Dust to dust. Career path to void 🌫️",
  "It's not you, it's the career path. Goodbye forever 💔",
  "Obliterated! Your career options have been reshuffled 🎲",
  "That path has gracefully exited the stage 🎭",
  "Whoosh — gone! Plot twist loading... ⚡",
  "Cleared! Room made for something legendary 🌟",
];

const randomDeleteMsg = () => DELETE_MESSAGES[Math.floor(Math.random() * DELETE_MESSAGES.length)];

// ─── Confirmation Modal ───────────────────────────────────────────────────────
function DeleteConfirmModal({ pathTitle, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-white rounded-3xl shadow-2xl p-6 max-w-sm w-full space-y-4">
        <div className="text-center space-y-1">
          <div className="w-14 h-14 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-2">
            <AlertTriangle className="w-7 h-7 text-red-500" />
          </div>
          <h3 className="text-xl font-black text-slate-900">Remove this path?</h3>
          <p className="text-slate-500 text-sm">
            <span className="font-semibold text-slate-700">"{pathTitle}"</span> will be removed from your career paths. This cannot be undone.
          </p>
        </div>
        <p className="text-center text-xs text-slate-400 font-normal flex items-center justify-center gap-1">
          <Ban className="w-3 h-3 inline-block" /> This is irreversible — all progress on this path will be lost.
        </p>
        <div className="flex gap-3 pt-1">
          <button
            onClick={onCancel}
            className="flex-1 py-3 rounded-2xl border-2 border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-600 font-bold text-sm transition-all active:scale-95"
          >
            Keep it
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-3 rounded-2xl bg-red-500 hover:bg-red-600 text-white font-bold text-sm transition-all active:scale-95 shadow-lg shadow-red-100"
          >
            Yes, remove
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Toast notification ───────────────────────────────────────────────────────
function DeleteToast({ message, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3500);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-sm w-full px-4">
      <div className="bg-slate-900 text-white rounded-2xl px-5 py-4 shadow-2xl flex items-center gap-3 animate-[slide-up_0.3s_ease-out]">
        <Trash2 className="w-5 h-5 shrink-0 text-slate-400" />
        <p className="text-sm font-medium leading-snug">{message}</p>
      </div>
    </div>
  );
}

// ─── Map broad path IDs to CareerPathCard-compatible shape ───────────────────
const BROAD_PATH_META = {
  broad_stem_tech: {
    category: "Technology",
    categoryColor: "text-blue-500",
    title: "STEM & Technology",
    description: "Engineering, computing, mathematics and pure sciences — building and understanding the technical world.",
    imageUrl: "/old-assets/game-developer.avif",
  },
  broad_health_medical: {
    category: "Health",
    categoryColor: "text-emerald-500",
    title: "Health & Medical Sciences",
    description: "Medicine, nursing, pharmacy and public health — keeping people and communities well.",
    imageUrl: "/old-assets/vet-doctor.avif",
  },
  broad_creative_arts: {
    category: "Creative",
    categoryColor: "text-pink-500",
    title: "Creative Arts & Design",
    description: "Fine arts, music, fashion and media — expressing the full range of human experience.",
    imageUrl: "/old-assets/animators.avif",
  },
  broad_business_finance: {
    category: "Business",
    categoryColor: "text-amber-500",
    title: "Business & Finance",
    description: "Accounting, banking, marketing and entrepreneurship — creating and managing economic value.",
    imageUrl: "/old-assets/chef.avif",
  },
  broad_humanities_social: {
    category: "Humanities",
    categoryColor: "text-violet-500",
    title: "Humanities & Social Sciences",
    description: "Communication, psychology, sociology and history — understanding people and the societies they build.",
    imageUrl: "/old-assets/marine-biologist.avif",
  },
  broad_law_governance: {
    category: "Law",
    categoryColor: "text-slate-500",
    title: "Law & Governance",
    description: "Law, politics, public administration and diplomacy — ordering society through rules and institutions.",
    imageUrl: "/old-assets/astronaut.avif",
  },
  broad_education_training: {
    category: "Education",
    categoryColor: "text-cyan-500",
    title: "Education & Training",
    description: "Teaching and training at every level — shaping the next generation of Nigerians.",
    imageUrl: "/old-assets/animation.avif",
  },
  broad_agriculture_environment: {
    category: "Agriculture",
    categoryColor: "text-lime-500",
    title: "Agriculture & Environment",
    description: "Farming, food science, fisheries and environmental management — sustaining life on land and water.",
    imageUrl: "/old-assets/marine-biologist.avif",
  },
};

/**
 * 3D ANIMATED TOOLTIP COMPONENT
 */
const Animated3DTooltip = ({
  index,
  step,
  backProps,
  primaryProps,
  skipProps,
  tooltipProps,
  isLastStep,
}) => (
  <div
    {...tooltipProps}
    className="tour-3d-box"
    style={{
      backgroundColor: "#ffffff",
      padding: "28px",
      borderRadius: "40px",
      border: "1px solid rgba(255, 255, 255, 0.5)",
      boxShadow:
        "0 20px 40px rgba(22, 154, 144, 0.15), 0 0 0 8px rgba(22, 154, 144, 0.05), inset 0 -8px 0 rgba(0, 0, 0, 0.05)",
      maxWidth: "380px",
      position: "relative",
    }}
  >
    <div style={{ position: "relative", zIndex: 1 }}>
      {step.title && (
        <h3
          style={{
            color: "#1e293b",
            fontSize: "22px",
            fontWeight: "900",
            marginBottom: "10px",
            letterSpacing: "-0.5px",
          }}
        >
          {step.title}
        </h3>
      )}
      <div
        style={{
          color: "#475569",
          fontWeight: "500",
          fontSize: "16px",
          marginBottom: "30px",
          lineHeight: "1.5",
        }}
      >
        {step.content}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button
          {...skipProps}
          style={{
            color: "#94a3b8",
            fontWeight: "700",
            fontSize: "13px",
            textTransform: "uppercase",
            background: "none",
            border: "none",
            cursor: "pointer",
          }}
        >
          Skip
        </button>
        <div style={{ display: "flex", gap: "14px" }}>
          {index > 0 && (
            <button
              {...backProps}
              className="btn-3d-secondary"
              style={{
                backgroundColor: "#f1f5f9",
                border: "none",
                borderRadius: "20px",
                padding: "10px 20px",
                fontWeight: "800",
                color: "#64748b",
                cursor: "pointer",
                boxShadow: "0 4px 0 #cbd5e1",
              }}
            >
              Back
            </button>
          )}
          <button
            {...primaryProps}
            className="btn-3d-primary"
            style={{
              background: "linear-gradient(180deg, #169a90 0%, #0d7a72 100%)",
              border: "none",
              borderRadius: "20px",
              padding: "10px 24px",
              fontWeight: "800",
              color: "white",
              cursor: "pointer",
              boxShadow: "0 6px 0 #085a54, 0 12px 20px rgba(22, 154, 144, 0.3)",
            }}
          >
            {isLastStep ? "Next Destination" : "Next"}
          </button>
        </div>
      </div>
    </div>
  </div>
);

export default function CareerPathPage() {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [runTour, setRunTour] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null); // { id, title }
  const [toastMsg, setToastMsg] = useState(null);

  // Reactive reads from StudentStore — updates automatically when BroadPathSelection saves
  const studentProfile = StudentStore((s) => s.student);
  const selectedBroadPaths = StudentStore((s) => s.student?.selectedBroadPaths ?? EMPTY_ARR);
  const suggestedBroadPaths = StudentStore((s) => s.student?.suggestedBroadPaths ?? EMPTY_ARR);
  const activeNodes = StudentStore((s) => s.student?.activeNodes ?? EMPTY_ARR);

  // Flat nodeId → { name, level, parentId } map built once from the career tree
  const nodeMap = useMemo(() => {
    const map = {};
    for (const broad of CAREER_TREE) {
      map[broad.nodeId] = { name: broad.name, level: "broad", parentId: null, description: broad.description ?? "", broadCategory: broad.broadCategory ?? "" };
      for (const field of broad.children ?? []) {
        map[field.nodeId] = { name: field.name, level: "field", parentId: broad.nodeId, description: field.description ?? "", broadCategory: field.broadCategory ?? broad.broadCategory ?? "" };
        for (const spec of field.children ?? []) {
          map[spec.nodeId] = { name: spec.name, level: "specialization", parentId: field.nodeId, description: spec.description ?? "", broadCategory: spec.broadCategory ?? field.broadCategory ?? broad.broadCategory ?? "" };
        }
      }
    }
    return map;
  }, []);

  // Derive display cards from the reactive store data.
  // selectedBroadPaths now stores actual picked node IDs (broad, field, or spec).
  const careerPaths = useMemo(() =>
    selectedBroadPaths.map((nodeId) => {
      // Broad-level pick — use BROAD_PATH_META directly
      if (BROAD_PATH_META[nodeId]) {
        const meta = BROAD_PATH_META[nodeId];
        const match = suggestedBroadPaths.find((m) => m?.nodeId === nodeId);
        let focusField = null;
        let focusSpec = null;
        for (const an of activeNodes) {
          const info = nodeMap[an.nodeId];
          if (!info) continue;
          if (info.level === "field" && info.parentId === nodeId) {
            focusField = info.name;
          } else if (info.level === "specialization") {
            const fieldInfo = nodeMap[info.parentId];
            if (fieldInfo?.parentId === nodeId) {
              focusField = fieldInfo.name;
              focusSpec = info.name;
            }
          }
        }
        const focusLabel = focusSpec ? `${focusField} › ${focusSpec}` : focusField ?? null;
        return { id: nodeId, ...meta, broadCategory: nodeMap[nodeId]?.broadCategory ?? "", progress: match?.matchPercentage ?? 0, focusLabel };
      }

      // Field or specialization pick — inherit color/category from parent broad
      const info = nodeMap[nodeId];
      if (!info) {
        return { id: nodeId, category: "Career", categoryColor: "text-slate-500", title: nodeId, description: "Your chosen career path.", broadCategory: "", progress: 0, focusLabel: null };
      }

      let broadId = null;
      if (info.level === "field") {
        broadId = info.parentId;
      } else {
        // specialization → parent is a field → field's parent is the broad
        const fieldInfo = nodeMap[info.parentId];
        broadId = fieldInfo?.parentId ?? null;
      }
      const broadMeta = BROAD_PATH_META[broadId] ?? { category: "Career", categoryColor: "text-slate-500" };
      const focusLabel = info.level === "specialization" ? (nodeMap[info.parentId]?.name ?? null) : null;

      return {
        id: nodeId,
        category: broadMeta.category,
        categoryColor: broadMeta.categoryColor,
        title: info.name,
        description: info.description || `${info.level === "field" ? "Field" : "Role"} within ${BROAD_PATH_META[broadId]?.title ?? "your career domain"}`,
        broadCategory: info.broadCategory,
        progress: 0,
        focusLabel,
      };
    }),
  [selectedBroadPaths, suggestedBroadPaths, activeNodes, nodeMap]);

  useEffect(() => {
    const hasTakenTour = localStorage.getItem("hasTakenTour_/career-path");
    if (!hasTakenTour) {
      setRunTour(true);
    }
  }, []);

  const tourSteps = [
    {
      target: ".mb-6.sm\\:mb-8.overflow-x-auto",
      title: "Daily Challenges",
      content: "Take the Career Assessment quiz anytime to unlock new career paths!",
      placement: "bottom",
    },
    {
      target: ".grid",
      title: "Your Journey",
      content: "These are your active career paths. Track your progress and keep exploring!",
      placement: "top",
    },
    {
      target: ".bg-slate-50",
      title: "Dream Big!",
      content: "Don't see what you're looking for? Add a custom path to your journey.",
      placement: "left",
    },
  ];

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      localStorage.setItem("hasTakenTour_/career-path", "true");
      setRunTour(false);
    }
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const closeSidebar = () => setIsSidebarOpen(false);

  // Step 1: show confirmation modal
  const handleDelete = (id) => {
    const path = careerPaths.find((p) => p.id === id);
    setPendingDelete({ id, title: path?.title ?? "this path" });
  };

  // Step 2: confirmed — remove from StudentStore + persist everywhere
  const confirmDelete = () => {
    if (!pendingDelete) return;
    const { id } = pendingDelete;

    const newPaths = selectedBroadPaths.filter((bid) => bid !== id);

    // Remove the node itself AND all descendants (works for broad, field, or spec)
    const isDescendantOf = (nodeId, targetId) => {
      const info = nodeMap[nodeId];
      if (!info) return false;
      if (info.parentId === targetId) return true; // direct child
      const parentInfo = nodeMap[info.parentId];
      return parentInfo?.parentId === targetId; // grandchild
    };
    const newNodes = (studentProfile?.activeNodes ?? []).filter(
      (n) => n?.nodeId !== id && !isDescendantOf(n?.nodeId, id),
    );

    // Update Zustand store (triggers reactive re-render of careerPaths above)
    StudentStore.getState().updateStudent({
      selectedBroadPaths: newPaths,
      activeNodes: newNodes,
    });

    // Keep StorageService in sync regardless of mode so subsequent saves read
    // the correct (post-delete) state instead of stale prototype-seeded data.
    const currentUser = AuthStore.getState().currentUser;
    if (currentUser) {
      const students = StorageService.read("students") ?? [];
      const idx = students.findIndex((s) => s.userId === currentUser._id || s.email === currentUser.email);
      if (idx !== -1) {
        students[idx] = { ...students[idx], selectedBroadPaths: newPaths, activeNodes: newNodes };
        StorageService.write("students", students);
      }
    }
    // In non-prototype mode also sync to backend
    if (config.appMode !== "prototype") {
      api.patch("/students/me", { selectedBroadPaths: newPaths, activeNodes: newNodes })
        .catch((err) => {
          console.error("[CareerPath] delete sync failed:", err);
          toast.error("Couldn't save changes to your account. Check your connection and try again.");
        });
    }

    // Clean up journey records + linked goals for the removed path
    pathEngine.onPathRemoved(id);

    setPendingDelete(null);
    setToastMsg(randomDeleteMsg());
  };
  const handleExplore = (id) => navigate(`/career-learning-path?id=${id}`);
  const handleAddMore = () => navigate("/broad-path-selection?mode=add");
  const handleSearch = (query) => console.log("Search:", query);

  return (
    <ProtectedRoute>
      {pendingDelete && (
        <DeleteConfirmModal
          pathTitle={pendingDelete.title}
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}
      {toastMsg && (
        <DeleteToast message={toastMsg} onDone={() => setToastMsg(null)} />
      )}
      <style>{`
        @keyframes boxPopIn {
          0% { transform: scale(0.9) translateY(20px); opacity: 0; }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }
        @keyframes scale-in {
          0% { transform: scale(0.85); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes slide-up {
          0% { transform: translateX(-50%) translateY(20px); opacity: 0; }
          100% { transform: translateX(-50%) translateY(0); opacity: 1; }
        }
        .tour-3d-box { animation: boxPopIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1); }
        .btn-3d-primary:active { transform: translateY(3px); }
        .btn-3d-secondary:active { transform: translateY(2px); }
      `}</style>

      <div className="flex min-h-screen bg-slate-200">
        <UniversalSearch onSearch={handleSearch} />
        <Sidebar />
        

        <div className="flex-1 lg:ml-64 transition-all duration-300 ease-in-out">
          {isSidebarOpen && (
            <div
              className="fixed inset-0 z-40 bg-black/10 backdrop-blur-sm md:hidden transition-all duration-300"
              onClick={closeSidebar}
            />
          )}

          <main
            className={`p-3 sm:p-4 md:p-6 lg:p-8 overflow-y-auto w-full min-h-screen transition-all duration-300 ${
              isSidebarOpen ? "blur-sm pointer-events-none md:blur-0 md:pointer-events-auto" : ""
            }`}
          >
            {/* Mobile Header */}
            <div className="md:hidden flex items-center gap-3 mb-4 pb-4 border-b border-slate-200">
              <button
                onClick={toggleSidebar}
                className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors min-h-11 min-w-11 flex items-center justify-center"
              >
                <svg className="w-6 h-6 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div className="flex-1">
                <h1 className="text-lg font-bold text-slate-900">Career Paths</h1>
                <p className="text-sm text-slate-600">
                  {studentProfile ? `Welcome back, ${studentProfile.firstName}` : "Welcome back"}
                </p>
              </div>
            </div>

            <div className="max-w-6xl mx-auto">
              <div className="hidden md:block">
                <CareerPathHeader userName={studentProfile?.firstName ?? "there"} />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
                <div className="flex flex-col gap-1 sm:gap-2">
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tighter text-slate-900">
                    My Career Paths
                  </h1>
                  <p className="text-sm sm:text-base text-slate-500">
                    Your journey to a fun future starts here!
                  </p>
                </div>
                <div className="flex h-8 sm:h-9 items-center justify-center rounded-full bg-blue-600/20 px-3 sm:px-4">
                  <p className="text-xs sm:text-sm font-bold text-blue-600">
                    Showing {careerPaths.length} Path{careerPaths.length !== 1 ? "s" : ""}
                  </p>
                </div>
              </div>

              <div className="mb-6 sm:mb-8 overflow-x-auto">
                <CareerPathBanner onQuizClick={() => navigate("/broad-path-selection?mode=add")} />
              </div>

              {careerPaths.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="text-5xl mb-4">🗺️</div>
                  <h3 className="text-xl font-black text-slate-800 mb-2">No paths chosen yet</h3>
                  <p className="text-slate-500 text-sm mb-6 max-w-xs">
                    Complete the psychometric assessment to get matched to your best-fit career paths.
                  </p>
                  <button
                    onClick={() => navigate("/psychometric-assessment")}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-3 rounded-full transition-all"
                  >
                    Take the Assessment
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {careerPaths.map((path) => (
                    <CareerPathCard
                      key={path.id}
                      id={path.id}
                      category={path.category}
                      categoryColor={path.categoryColor}
                      title={path.title}
                      description={path.description}
                      broadCategory={path.broadCategory}
                      progress={path.progress}
                      focusLabel={path.focusLabel}
                      onExplore={() => handleExplore(path.id)}
                      onDelete={handleDelete}
                    />
                  ))}
                  <AddCareerPathCard onAdd={handleAddMore} />
                </div>
              )}
            </div>
          </main>
        </div>

        <Joyride
          steps={tourSteps}
          run={runTour}
          continuous={true}
          showProgress={true}
          showSkipButton={true}
          callback={handleJoyrideCallback}
          tooltipComponent={Animated3DTooltip}
          floaterProps={{ disableAnimation: true }}
          styles={{
            options: {
              zIndex: 10000,
              overlayColor: "rgba(15, 23, 42, 0.65)",
            },
            spotlight: { borderRadius: "24px" },
          }}
        />
      </div>
    </ProtectedRoute>
  );
}
